import type { StepLayoutGroup, ZipTie, ZipTieWire } from "../types";
import {
  parseStepLayoutGroups,
  pruneStepLayoutGroupMembers,
  remapStepLayoutGroupStepIds,
} from "./stepLayoutGroups";
import {
  parseZipTies,
  pruneZipTies,
  collectZipTieWireKeys,
  remapZipTieStepIds,
  type ZipTieExpressionStep,
} from "./zipTies";

/**
 * Canvas-only decoration stored beside a workflow graph: step layout groups and
 * zip ties. The engine never reads either. Every layer that copies, remaps, or
 * prunes a graph goes through these helpers, so adding a decoration means
 * adding it here rather than to each layer.
 *
 * The fields stay flat on the wire (`stepLayoutGroups`, `zipTies`) so existing
 * files and API clients keep their shape.
 */
export type CanvasLayout = {
  stepLayoutGroups: StepLayoutGroup[];
  zipTies: ZipTie[];
};

/** The graph a layout is pruned against: the steps (with sockets) and edges that exist. */
export type CanvasLayoutGraph = {
  steps: readonly ZipTieExpressionStep[];
  edges: readonly ZipTieWire[];
};

/** Coerce untrusted values (JSONB columns, an imported file) into a layout. */
export function parseCanvasLayout(source: {
  stepLayoutGroups?: unknown;
  zipTies?: unknown;
}): CanvasLayout {
  return {
    stepLayoutGroups: parseStepLayoutGroups(source.stepLayoutGroups),
    zipTies: parseZipTies(source.zipTies),
  };
}

/** Rewrite step ids after a clone gives every step a fresh id. */
export function remapCanvasLayoutStepIds(
  layout: CanvasLayout,
  stepIdMap: Map<string, string>,
): CanvasLayout {
  return {
    stepLayoutGroups: remapStepLayoutGroupStepIds(
      layout.stepLayoutGroups,
      stepIdMap,
    ),
    zipTies: remapZipTieStepIds(layout.zipTies, stepIdMap),
  };
}

/** Drop references to steps and wires that are not in `graph`. */
export function pruneCanvasLayout(
  layout: CanvasLayout,
  graph: CanvasLayoutGraph,
): CanvasLayout {
  return {
    stepLayoutGroups: pruneStepLayoutGroupMembers(
      layout.stepLayoutGroups,
      graph.steps.map((step) => step.id),
    ),
    zipTies: pruneZipTies(
      layout.zipTies,
      collectZipTieWireKeys(graph.edges, graph.steps),
    ),
  };
}
